import type { CSSProperties, MouseEvent } from "react";
import type { Segment } from "./segments.js";
import { formatClock } from "@heroncendre/player";
import type { Guide } from "./useGuide.js";
import { useChaines } from "./useChaines.js";
import { BoutonInfos } from "../info/BoutonInfos.js";
import { GuideSubtitles } from "./GuideSubtitles.js";
import {
  AVENIR,
  COULEURS,
  CREME,
  SAUGE,
  contour,
  ellipse,
  POLICE_UI_SPLASH,
  carre,
  rond,
  surtitre,
  titreItalien,
} from "./guideStyles.js";
import {
  IconeLecture,
  IconePause,
  IconePrecedent,
  IconeSon,
  IconeSuivant,
  IconeChevronBas,
  Losange,
} from "./icones.js";

/**
 * Le contenu des bandes : en haut, où l'on en est dans le guide ; en bas, la voix
 * (sous-titres, barre segmentée, commandes). Mise en page du handoff : trois
 * colonnes sur ordinateur, deux rangées sur téléphone.
 */

/* --- bande haute ----------------------------------------------------------- */

export function BandeHaute({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  const { t, parcours } = useChaines();
  const total = guide.chapitres.length;
  const n = guide.indexChapitre + 1;
  const titre = guide.chapitre?.titre ?? "";
  const quitter = (
    <button type="button" style={contour} onClick={() => guide.actions.quitter()}>
      {etroit ? t("quitShort") : t("quit")}
    </button>
  );

  if (etroit) {
    return (
      <div style={{ ...rangeeHaute, gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ ...surtitre, fontSize: 10 }}>
            <Losange couleur={SAUGE} />
            {t("chapter")} {t("chapterOf", { n, total })}
          </span>
          <button
            type="button"
            className="gd-titre-bouton"
            aria-haspopup="dialog"
            aria-label={t("openChapters")}
            onClick={(event) => guide.actions.ouvrirMenu("titre", event.currentTarget)}
            style={{ ...boutonTitre, justifyContent: "flex-start", margin: "0 -8px", minWidth: 0 }}
          >
            <span style={{ ...titreItalien(21), ...ellipse, minWidth: 0 }}>{titre}</span>
            <IconeChevronBas taille={16} />
          </button>
        </div>
        {quitter}
      </div>
    );
  }

  return (
    <div style={{ ...rangeeHaute, display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center" }}>
      <span style={{ ...surtitre, justifySelf: "start" }}>
        <Losange couleur={SAUGE} />
        {t("guided", { parcours: parcours(guide.route) })}
      </span>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0, minWidth: 0 }}>
        <span style={{ ...surtitre, gap: 10, lineHeight: "14px", height: 14 }}>
          {t("chapter")} {t("chapterOf", { n, total })}
          <span style={{ display: "inline-flex", gap: 6 }} aria-hidden>
            {guide.chapitres.map((c) => (
              <Losange
                key={c.index}
                couleur={c.index < guide.indexChapitre ? SAUGE : c.index === guide.indexChapitre ? CREME : AVENIR}
              />
            ))}
          </span>
        </span>
        <button
          type="button"
          className="gd-titre-bouton"
          aria-haspopup="dialog"
          aria-label={t("openChapters")}
          onClick={(event) => guide.actions.ouvrirMenu("titre", event.currentTarget)}
          style={{ ...boutonTitre, maxWidth: "min(520px, 46vw)" }}
        >
          <span style={{ ...titreItalien(26), ...ellipse }}>{titre}</span>
          <IconeChevronBas taille={18} />
        </button>
      </div>
      {/* Le menu d'informations, à gauche de « Quitter le guide » (handoff `design_handoff_credits`). */}
      <div style={{ justifySelf: "end", display: "flex", alignItems: "center", gap: 12 }}>
        <BoutonInfos variante="info" habillage="bande" />
        {quitter}
      </div>
    </div>
  );
}

/** Le titre du chapitre, bouton : texte, chevron, et un fond au survol (`guide.css`). */
const boutonTitre: CSSProperties = {
  appearance: "none",
  height: 32,
  padding: "0 10px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  color: CREME,
  cursor: "pointer",
  minWidth: 0,
};

const rangeeHaute: CSSProperties = {
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  padding: "0 28px",
};

/* --- bande basse ----------------------------------------------------------- */

export function BandeBasse({ guide, etroit, fin = false }: { guide: Guide; etroit: boolean; fin?: boolean }) {
  const { t } = useChaines();
  const { scene, indexScene, segments } = guide;
  const nom = guide.nomScene(scene?.sceneId);
  const tempsScene = scene ? Math.max(0, Math.min(scene.duree, guide.temps - scene.debut)) : 0;
  const numero = segments.length ? String(indexScene + 1).padStart(2, "0") : "";

  const temps = (
    <span style={{ fontSize: etroit ? 12 : 12.5, color: "#E3D9C8", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
      {formatClock(tempsScene)} / {formatClock(scene?.duree ?? 0)}
      {!etroit && (
        <span style={{ opacity: 0.6 }}>
          {"  ·  "}
          {formatClock(guide.temps)} / {formatClock(guide.chapitre?.duree ?? guide.duree)}
        </span>
      )}
    </span>
  );

  const commandes = fin ? (
    <span style={{ ...surtitre, fontSize: 12, justifyContent: "center" }}>
      <Losange couleur={SAUGE} />
      {t("chapterDone")}
    </span>
  ) : (
    <div style={{ display: "flex", alignItems: "center", gap: etroit ? 14 : 18 }}>
      <button type="button" aria-label={t("prevScene")} style={rond(44)} onClick={guide.actions.scenePrecedente}>
        <IconePrecedent />
      </button>
      <button
        type="button"
        aria-label={guide.lecture ? t("pause") : t("play")}
        style={rond(etroit ? 56 : 48, true)}
        onClick={guide.actions.basculerLecture}
      >
        {guide.lecture ? <IconePause taille={22} /> : <IconeLecture taille={22} />}
      </button>
      <button type="button" aria-label={t("nextScene")} style={rond(44)} onClick={guide.actions.sceneSuivante}>
        <IconeSuivant />
      </button>
    </div>
  );

  const options = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <BoutonSon guide={guide} />
      <BoutonSousTitres guide={guide} />
    </div>
  );

  // Ordinateur, du haut de la bande (136 px) : 14 · sous-titres 22 (une ligne réservée, une
  // seconde grandit vers le haut) · 8 · barre 18 · 8 · rangée 44 · 22. Même rythme en fin de
  // chapitre : la barre ne bouge pas quand on passe de l'un à l'autre.
  return (
    <div style={{ ...colonneBasse, justifyContent: etroit ? "center" : "flex-start", padding: etroit ? "8px 0" : "14px 0 22px", gap: etroit ? 6 : 8 }}>
      <GuideSubtitles
        actif={guide.soustitres && !fin}
        style={{
          fontSize: etroit ? 13.5 : 16,
          padding: "0 28px",
          ...(etroit
            ? { minHeight: 13.5 * 1.35 * 2 }
            : { height: 22, flex: "none", display: "flex", alignItems: "flex-end", justifyContent: "center" }),
        }}
      />
      <BarreSegmentee guide={guide} plein={fin} />
      {etroit ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "0 16px" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
            <span style={{ ...titreItalien(17), ...ellipse, minWidth: 0 }}>
              <span style={{ color: SAUGE, fontSize: 12, marginRight: 8, fontFamily: "inherit" }}>{numero}</span>
              {nom}
            </span>
            {temps}
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: fin ? "space-between" : "space-between" }}>
            <BoutonSousTitres guide={guide} />
            {commandes}
            {/* Le son, et le ⋯ du menu d'informations à sa droite (handoff `design_handoff_credits`). */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BoutonSon guide={guide} />
              <BoutonInfos variante="plus" habillage="bande" />
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", padding: "0 28px", height: 44 }}>
          <span style={{ ...titreItalien(20), ...ellipse, justifySelf: "start", maxWidth: "100%", minWidth: 0 }}>
            <span style={{ color: SAUGE, fontSize: 13, marginRight: 10, fontFamily: "inherit" }}>{numero}</span>
            {nom}
          </span>
          {commandes}
          <div style={{ justifySelf: "end", display: "flex", alignItems: "center", gap: 18 }}>
            {temps}
            {options}
          </div>
        </div>
      )}
    </div>
  );
}

/** Fin de chapitre : la même bande, barre pleine, « Chapitre terminé » à la place des commandes. */
export function BandeBasseFin({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  return <BandeBasse guide={guide} etroit={etroit} fin />;
}

const colonneBasse: CSSProperties = {
  height: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: "8px 0",
};

/* --- barre segmentée -------------------------------------------------------- */

/**
 * L'instant visé par un clic sur un segment : le POINT cliqué, pas le début de la scène — sinon
 * toucher la scène en cours la rejouerait depuis son premier mot. Au clavier (Entrée, Espace :
 * `detail` vaut 0, pas de position), on va au début du segment.
 */
function instantClique(segment: Segment, event: MouseEvent<HTMLButtonElement>): number {
  if (event.detail === 0) return segment.debut;
  const cadre = event.currentTarget.getBoundingClientRect();
  if (cadre.width <= 0) return segment.debut;
  const fraction = Math.max(0, Math.min(1, (event.clientX - cadre.left) / cadre.width));
  return segment.debut + fraction * segment.duree;
}

/**
 * Un segment par scène, de largeur proportionnelle à sa durée, 3 px d'écart. Chaque
 * segment est un bouton de 18 px de haut — une vraie cible — qui emmène à sa scène ;
 * le trait visible (3 px, 4 px pour le courant) est dessiné dedans.
 */
export function BarreSegmentee({ guide, plein = false }: { guide: Guide; plein?: boolean }) {
  const { t } = useChaines();
  const { segments, temps } = guide;
  if (segments.length === 0) return null;
  return (
    <div role="group" aria-label={t("guideBar")} style={{ display: "flex", gap: 3, padding: "0 28px", boxSizing: "border-box" }}>
      {segments.map((segment) => {
        const courant = segment.index === guide.indexScene;
        const rempli = plein ? 1 : segment.duree === 0 ? 0 : Math.max(0, Math.min(1, (temps - segment.debut) / segment.duree));
        return (
          <button
            key={segment.index}
            type="button"
            aria-label={t("goToScene", { n: segment.index + 1, nom: guide.nomScene(segment.sceneId) })}
            aria-current={courant && !plein ? "step" : undefined}
            disabled={plein}
            onClick={(event) => guide.actions.aller(instantClique(segment, event))}
            style={{
              appearance: "none",
              flex: `${Math.max(segment.duree, 0.1)} 1 0`,
              minWidth: 6,
              height: 18,
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: plein ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              aria-hidden
              style={{
                display: "block",
                width: "100%",
                height: courant && !plein ? 4 : 3,
                background: "rgba(246,238,227,.22)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <span style={{ display: "block", height: "100%", width: `${rempli * 100}%`, background: CREME }} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* --- son et sous-titres ------------------------------------------------------ */

export function BoutonSon({ guide, style }: { guide: Guide; style?: CSSProperties }) {
  const { t } = useChaines();
  return (
    <button
      type="button"
      aria-label={guide.muet ? t("unmute") : t("mute")}
      aria-pressed={guide.muet}
      style={{ ...carre(44), ...style }}
      onClick={guide.actions.basculerSon}
    >
      <IconeSon coupe={guide.muet} />
    </button>
  );
}

/** CC actif = fond crème. Le libellé « CC » est écrit, pas dessiné. */
export function BoutonSousTitres({ guide, style }: { guide: Guide; style?: CSSProperties }) {
  const { t } = useChaines();
  return (
    <button
      type="button"
      aria-label={t("cc")}
      aria-pressed={guide.soustitres}
      style={{
        ...carre(44, guide.soustitres),
        fontFamily: POLICE_UI_SPLASH,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: ".08em",
        ...style,
      }}
      onClick={guide.basculerSoustitres}
    >
      CC
    </button>
  );
}
